import React, { useEffect } from 'react';
import { X, Briefcase, MapPin, GraduationCap, Building2, ExternalLink, User } from 'lucide-react';
import { CandidateMatchResult } from '../services/jobMatchingApi';
import { normalizeMatchScore } from '../../shared/utils/apiClient';

interface CandidateProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    match: CandidateMatchResult | null;
}

const CandidateProfileModal: React.FC<CandidateProfileModalProps> = ({ isOpen, onClose, match }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    if (!isOpen || !match) return null;

    const c = match.candidate;
    const name = c.name || 'Unknown';
    const initials = name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
    const matchPercent = (() => {
        return Math.round(normalizeMatchScore(match.vectorSimilarity, match.qwenScore, match.softCriteriaScore) * 100);
    })();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="relative bg-gradient-to-br from-[#015FA6] to-[#0077cc] px-6 py-6">
                    <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/20 transition-colors">
                        <X size={18} className="text-white/80" />
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                            {initials}
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-xl font-semibold text-white truncate">{name}</h3>
                            {c.email && <p className="text-sm text-white/70 mt-0.5">{c.email}</p>}
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    match.source === 'imported'
                                        ? 'bg-amber-400/20 text-amber-100'
                                        : 'bg-white/20 text-white'
                                }`}>
                                    {match.source === 'imported' ? 'Imported' : 'Platform'}
                                </span>
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white">
                                    {matchPercent}% match
                                </span>
                                {c.linkedinUrl && (
                                    <a
                                        href={c.linkedinUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-white/80 hover:text-white transition-colors"
                                    >
                                        <ExternalLink size={12} />
                                        LinkedIn
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)] space-y-5">
                    {/* Summary row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {c.yearsExp != null && (
                            <div className="bg-gray-50 rounded-xl p-3 text-center">
                                <Briefcase size={16} className="mx-auto text-[#015FA6] mb-1" />
                                <div className="text-lg font-bold text-[#0B111E]">{c.yearsExp}+</div>
                                <div className="text-[10px] text-[#757575] uppercase tracking-wider">Years Exp</div>
                            </div>
                        )}
                        {c.locations && c.locations.length > 0 && (
                            <div className="bg-gray-50 rounded-xl p-3 text-center">
                                <MapPin size={16} className="mx-auto text-[#015FA6] mb-1" />
                                <div className="text-sm font-semibold text-[#0B111E] truncate">{c.locations[0]}</div>
                                <div className="text-[10px] text-[#757575] uppercase tracking-wider">Location</div>
                            </div>
                        )}
                        {c.degreesList && c.degreesList.length > 0 && (
                            <div className="bg-gray-50 rounded-xl p-3 text-center">
                                <GraduationCap size={16} className="mx-auto text-[#015FA6] mb-1" />
                                <div className="text-sm font-semibold text-[#0B111E] truncate">{c.degreesList[0]}</div>
                                <div className="text-[10px] text-[#757575] uppercase tracking-wider">Education</div>
                            </div>
                        )}
                        {c.allCompanies && c.allCompanies.length > 0 && (
                            <div className="bg-gray-50 rounded-xl p-3 text-center">
                                <Building2 size={16} className="mx-auto text-[#015FA6] mb-1" />
                                <div className="text-sm font-semibold text-[#0B111E] truncate">{c.allCompanies[0]}</div>
                                <div className="text-[10px] text-[#757575] uppercase tracking-wider">Company</div>
                            </div>
                        )}
                    </div>

                    {/* Job Titles */}
                    {c.allJobTitles && c.allJobTitles.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-[#757575] uppercase tracking-wider mb-2">
                                <User size={12} className="inline mr-1 -mt-0.5" />
                                Job Titles
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {c.allJobTitles.map((title, i) => (
                                    <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium bg-[#E3F2FD] text-[#015FA6]">
                                        {title}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Skills */}
                    {c.skills && c.skills.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-[#757575] uppercase tracking-wider mb-2">
                                Skills
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {c.skills.map((skill, i) => (
                                    <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-[#015FA6] border border-blue-100">
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Companies */}
                    {c.allCompanies && c.allCompanies.length > 1 && (
                        <div>
                            <h4 className="text-xs font-semibold text-[#757575] uppercase tracking-wider mb-2">
                                <Building2 size={12} className="inline mr-1 -mt-0.5" />
                                Companies
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {c.allCompanies.map((company, i) => (
                                    <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-[#0B111E] border border-gray-100">
                                        {company}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Degrees */}
                    {c.degreesList && c.degreesList.length > 1 && (
                        <div>
                            <h4 className="text-xs font-semibold text-[#757575] uppercase tracking-wider mb-2">
                                <GraduationCap size={12} className="inline mr-1 -mt-0.5" />
                                Education
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {c.degreesList.map((degree, i) => (
                                    <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                                        {degree}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Locations */}
                    {c.locations && c.locations.length > 1 && (
                        <div>
                            <h4 className="text-xs font-semibold text-[#757575] uppercase tracking-wider mb-2">
                                <MapPin size={12} className="inline mr-1 -mt-0.5" />
                                Locations
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {c.locations.map((loc, i) => (
                                    <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-[#0B111E] border border-gray-100">
                                        {loc}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Score Breakdown */}
                    <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="text-xs font-semibold text-[#757575] uppercase tracking-wider mb-3">
                            Match Score Breakdown
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-[#757575]">Vector Similarity</span>
                                <span className="font-medium text-[#0B111E]">{(match.vectorSimilarity * 100).toFixed(1)}%</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[#757575]">Qwen Re-rank</span>
                                <span className="font-medium text-[#0B111E]">{match.qwenScore.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[#757575]">Soft Criteria</span>
                                <span className="font-medium text-[#0B111E]">{match.softCriteriaScore.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[#757575]">Total Score</span>
                                <span className="font-bold text-[#015FA6]">{match.matchScore.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CandidateProfileModal;
